/**
 * Moderator Dashboard
 *
 * Presentational/controlled: the parent owns the navigation location (history)
 * and passes the current sub-view + handlers down. This lets the SAME component
 * be driven either by the standalone `ModeratorPortal` (moderator login) or by
 * the AdminDashboard (admin accessing moderator tools) without each spinning up
 * its own history instance — the history layer is identical regardless of role.
 */

import { PortalCard } from '../shell/portal-card';
import { Card } from '../../components/card';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../services/authentication/auth-store';
import { useHistoryLocation } from '../../hooks/use-history-location';
import { CommonHeader } from '../shell/common-header';
import { MatchModeration } from './match-moderation';
import { CrashReportLookup } from './crash-report-lookup';
import { Evaluation } from './evaluation';
import styles from '../shell/portal-dashboard.module.css';

export type ModeratorView = 'main' | 'match-moderation' | 'evaluation' | 'crash-support';

interface ModeratorDashboardProps {
  /** Which moderator view to render (driven by the owning history location). */
  subView: ModeratorView;
  /** Open one of the moderator tools (pushes a history entry in the owner). */
  onOpen: (view: Exclude<ModeratorView, 'main'>) => void;
  /** Go back one level (history.back in the owner). */
  onBack: () => void;
  /** Show a back button on the main dashboard (true when embedded in admin). */
  showMainBack?: boolean;
}

export function ModeratorDashboard({ subView, onOpen, onBack, showMainBack = false }: ModeratorDashboardProps) {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);

  if (subView === 'crash-support') return <CrashReportLookup onBack={onBack} />;
  if (subView === 'match-moderation') {
    return <MatchModeration onBack={onBack} />;
  }
  if (subView === 'evaluation') {
    return <Evaluation onBack={onBack} />;
  }

  return (
    <div className="portal-shell portal-container">
      <CommonHeader
        title={t('dashboard.title')}
        showBackButton={showMainBack}
        onBackClick={onBack}
      />

      <main className="portal-main">
        <div className="portal-content">
          <Card framed padded={false} className={styles.portalWelcome}>
            <h2 className={styles.portalWelcomeTitle}>
              {t('dashboard.welcome', { name: user?.name })}
            </h2>
            <p className={styles.portalWelcomeSubtitle}>
              {t('dashboard.comingSoon')}
            </p>
          </Card>

          <div className={styles.portalGrid}>
            <PortalCard title={t('crashSupport.title')} description={t('crashSupport.description')} icon="🔎" onClick={() => onOpen('crash-support')} />
            <PortalCard
              title={t('dashboard.cards.matchModeration.title')}
              description={t('dashboard.cards.matchModeration.description')}
              icon="🎮"
              onClick={() => onOpen('match-moderation')}
            />
            <PortalCard
              title={t('dashboard.cards.evaluation.title')}
              description={t('dashboard.cards.evaluation.description')}
              icon="📋"
              onClick={() => onOpen('evaluation')}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Standalone moderator portal (used when the logged-in user is a moderator).
 * Owns the history location so Back/Forward move through the moderator tools.
 */
export function ModeratorPortal() {
  const { location, navigate, back } = useHistoryLocation<{ view: ModeratorView }>({ view: 'main' });
  return (
    <ModeratorDashboard
      subView={location.view}
      onOpen={(view) => navigate({ view })}
      onBack={back}
      showMainBack={false}
    />
  );
}

